import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import jwtDecode from 'jwt-decode';
import Cookies from 'universal-cookie';
import { useNavigate } from 'react-router-dom';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import { useNotificationContext } from './NotificationContext';
import { LocalFrontRequest } from './Axios';
import {
    getEnvironmentVariable
} from './GetEnvVar';

const cookies = new Cookies();

function useInterval(callback, delay) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

const AutoRefresh = ({
    localStorageKey
}) => {
    const { t } = useTranslation();
    const { setNotification } = useNotificationContext();

    useEffect(() => {
        localStorage.removeItem(localStorageKey);
        LocalFrontRequest()
            .get('/version.json')
            .then((result) => {
                const remoteHash = result.data.gitHash;
                localStorage.setItem(localStorageKey, remoteHash);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [localStorageKey]);

    useInterval(() => {
        LocalFrontRequest()
            .get('/version.json')
            .then((result) => {
                const remoteHash = result.data.gitHash;
                const localHash = localStorage.getItem(localStorageKey);

                if (!localHash) {
                    localStorage.setItem(localStorageKey, remoteHash);
                    console.info(`${localStorageKey} git hash set to "${remoteHash}"`);
                    return;
                }

                if (remoteHash !== localHash) {
                    setNotification({
                        text: t('A new version is available. Please click here to reload your app.'),
                        id: 'please-reload-your-app',
                        onClick: () => {
                            localStorage.setItem(localStorageKey, remoteHash);
                            window.location.reload(true);
                        }
                    });
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, 30000);

    return null;
};

const AutoRefreshJWTToken = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const envPrefix = getEnvironmentVariable('envPrefix');

    const [refreshToken] = useMutation(gql`
    mutation RefreshToken {
        refreshToken(input: {}) {
            jwtToken
        }
    }
    `);

    const refreshJWT = useCallback(async() => {
        let jwtAccessName;
        switch (envPrefix) {
        case 'ADMIN-CONSOLE':
            jwtAccessName = 'jwtTokenAdminConsole';
            break;
        case 'EXTERNAL-CONSOLE':
            jwtAccessName = 'jwtTokenExternalConsole';
            break;
        default:
            break;
        }
        const existingJwtCookie = cookies.get(jwtAccessName);
        const existingRememberMeCookie = cookies.get('jwtTokenRememberMe');
        const oldDecodedJwt = jwtDecode(existingJwtCookie);

        if ((oldDecodedJwt?.exp ?? false) && new Date(oldDecodedJwt?.exp * 1000) < Date.now()) {
            return;
        }

        const result = await refreshToken();
        const newDecodedJwt = jwtDecode(result?.data?.refreshToken?.jwtToken);

        if (existingJwtCookie === cookies.get(jwtAccessName)) {
            cookies.set(jwtAccessName, result?.data?.refreshToken?.jwtToken, {
                domain: document.domain,
                path: '/',
                expires: existingRememberMeCookie === 'true' ? new Date(newDecodedJwt?.exp * 1000) : undefined
            });
        }
    }, [envPrefix, refreshToken]);

    useInterval(() => {
        refreshJWT()
            .catch((err) => {
                if (err.message === 'Blocked') {
                    navigate('/logout');
                    toast.error(t('You account is blocked. Please contact Spacefill to know more.'));
                }
            });
    }, 30000);

    return null;
};

export {
    AutoRefresh,
    AutoRefreshJWTToken
};
